import { Controller } from "@hotwired/stimulus";

// Searchable contact picker that scales: instead of rendering every contact,
// it queries a search endpoint and adds chosen contacts as removable chips
// (hidden contact_ids[] inputs). Dispatches "recipients:changed" so a
// surrounding cost/count controller can recompute.
export default class extends Controller {
  static targets = ["input", "results", "chips", "count"];
  static values = { url: String };

  connect() {
    this.timer = null;
    this.updateCount();
  }

  search() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 250);
  }

  async run() {
    const term = this.inputTarget.value.trim();
    if (!term) {
      this.resultsTarget.innerHTML = "";
      return;
    }
    const url = `${this.urlValue}?term=${encodeURIComponent(term)}`;
    const matches = await (
      await fetch(url, { headers: { Accept: "application/json" } })
    ).json();
    this.renderResults(matches);
  }

  renderResults(matches) {
    const chosen = this.chosenIds();
    const items = matches.filter((m) => !chosen.has(String(m.id)));
    if (items.length === 0) {
      this.resultsTarget.innerHTML = `<p class="px-3 py-2 text-xs text-gray-400">No more matches</p>`;
      return;
    }
    this.resultsTarget.innerHTML = items
      .map(
        (m) =>
          `<button type="button" data-action="recipient-search#pick" data-id="${m.id}" data-label="${this.escape(m.label)}" data-phone="${m.phone}"
             class="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
             ${this.escape(m.label)} <span class="text-gray-400">${m.phone}</span>
           </button>`,
      )
      .join("");
  }

  pick(event) {
    const { id, label, phone } = event.currentTarget.dataset;
    const chip = document.createElement("span");
    chip.className =
      "inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-xs";
    chip.dataset.id = id;
    chip.innerHTML = `
      <input type="hidden" name="contact_ids[]" value="${id}" data-bulk-sms-target="contactCheckbox" data-phone="${phone}">
      <span>${this.escape(label)}</span>
      <button type="button" data-action="recipient-search#remove" class="text-primary-500 hover:text-primary-700">&times;</button>`;
    this.chipsTarget.appendChild(chip);
    this.inputTarget.value = "";
    this.resultsTarget.innerHTML = "";
    this.changed();
  }

  remove(event) {
    event.currentTarget.closest("[data-id]").remove();
    this.changed();
  }

  changed() {
    this.updateCount();
    this.element.dispatchEvent(
      new CustomEvent("recipients:changed", { bubbles: true }),
    );
  }

  updateCount() {
    if (!this.hasCountTarget) return;
    const n = this.chipsTarget.querySelectorAll("[data-id]").length;
    this.countTarget.textContent =
      n === 1 ? "1 contact added" : `${n} contacts added`;
  }

  chosenIds() {
    return new Set(
      Array.from(this.chipsTarget.querySelectorAll("[data-id]")).map(
        (c) => c.dataset.id,
      ),
    );
  }

  escape(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
